
/*
 * Supported Keys: https://craig.is/killing/mice
 */
const bindings = [
  {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'Zoom' },
    label: 'Zoom',
    keys: ['z'],
    isEditable: true,
  },
  // EQUINE: tool selection from the keyboard. setToolActive binds the chosen
  // tool to the primary mouse button and demotes the previous one to passive,
  // so `w` is the way back to the default after any of the others.
  {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'WindowLevel' },
    label: 'Window/Level',
    keys: ['w'],
    isEditable: true,
  },
  {
    // Pan was middle-drag only, which a trackpad has no button for.
    commandName: 'setToolActive',
    commandOptions: { toolName: 'Pan' },
    label: 'Pan',
    keys: ['p'],
    isEditable: true,
  },
  {
    // Length is registered passive with no key. Measuring joint space and
    // fragment size is routine in a pre-purchase radiograph review.
    commandName: 'setToolActive',
    commandOptions: { toolName: 'Length' },
    label: 'Measure Length',
    keys: ['d'],
    isEditable: true,
  },
  {
    // Magnify keeps the whole image in view while examining bone margins,
    // which zoom does not. Registered in the tool group but disabled, so
    // this is the only way to reach it.
    commandName: 'setToolActive',
    commandOptions: { toolName: 'AdvancedMagnify' },
    label: 'Magnify',
    keys: ['m'],
    isEditable: true,
  },
  // EQUINE: move focus between viewports. `]` `[` `up` `down` all act on the
  // ACTIVE viewport, and in 2-up there was no keyboard way to change which
  // one that is -- it needed the mouse.
  //
  // Deliberately not Tab: Tab is how a keyboard user reaches the study
  // browser, the toolbar and the panels, and capturing it globally would cost
  // more than it buys. Change these two keys if you would rather have it.
  {
    commandName: 'incrementActiveViewport',
    label: 'Next Viewport',
    keys: ['n'],
    isEditable: true,
  },
  {
    commandName: 'decrementActiveViewport',
    label: 'Previous Viewport',
    keys: ['shift+n'],
    isEditable: true,
  },
  {
    commandName: 'scaleUpViewport',
    label: 'Zoom In',
    keys: ['+'],
    isEditable: true,
  },
  {
    commandName: 'scaleDownViewport',
    label: 'Zoom Out',
    keys: ['-'],
    isEditable: true,
  },
  {
    commandName: 'fitViewportToWindow',
    label: 'Zoom to Fit',
    keys: ['='],
    isEditable: true,
  },
  {
    commandName: 'rotateViewportCW',
    label: 'Rotate Right',
    keys: ['r'],
    isEditable: true,
  },
  {
    commandName: 'rotateViewportCCW',
    label: 'Rotate Left',
    keys: ['l'],
    isEditable: true,
  },
  {
    commandName: 'flipViewportHorizontal',
    label: 'Flip Horizontally',
    keys: ['h'],
    isEditable: true,
  },
  {
    commandName: 'flipViewportVertical',
    label: 'Flip Vertically',
    keys: ['v'],
    isEditable: true,
  },
  {
    commandName: 'toggleCine',
    label: 'Cine',
    keys: ['c'],
  },
  {
    commandName: 'invertViewport',
    label: 'Invert',
    keys: ['i'],
    isEditable: true,
  },
  // Left/Right and their bracket aliases advance the equine clinical protocol
  // stage in one-up and two-up layouts. Outside those layouts they use the
  // numeric SeriesNumber fallback for optional and unclassified images; a
  // missing clinical stage never falls back to series 1.
  {
    commandName: 'equineNavigateSeries',
    commandOptions: { direction: 1 },
    label: 'Next Series',
    keys: ['right', ']'],
    isEditable: true,
  },
  {
    commandName: 'equineNavigateSeries',
    commandOptions: { direction: -1 },
    label: 'Previous Series',
    keys: ['left', '['],
    isEditable: true,
  },
  // Protocol-stage navigation remains available without competing for the
  // arrow/bracket keys.
  {
    commandName: 'nextStage',
    context: 'DEFAULT',
    label: 'Next Stage',
    keys: ['pagedown', '.'],
    isEditable: true,
  },
  {
    commandName: 'previousStage',
    context: 'DEFAULT',
    label: 'Previous Stage',
    keys: ['pageup', ','],
    isEditable: true,
  },
  {
    commandName: 'nextImage',
    label: 'Next Image',
    keys: ['down'],
    isEditable: true,
  },
  {
    commandName: 'previousImage',
    label: 'Previous Image',
    keys: ['up'],
    isEditable: true,
  },
  {
    commandName: 'firstImage',
    label: 'First Image',
    keys: ['home'],
    isEditable: true,
  },
  {
    commandName: 'lastImage',
    label: 'Last Image',
    keys: ['end'],
    isEditable: true,
  },
  {
    commandName: 'resetViewport',
    label: 'Reset',
    keys: ['space'],
    isEditable: true,
  },
  {
    commandName: 'cancelMeasurement',
    label: 'Cancel Cornerstone Measurement',
    keys: ['esc'],
  },
  // EQUINE: 1-5 used to apply windowLevelPresets, which are stock OHIF CT
  // presets in Hounsfield units -- Soft tissue, Lung, Liver, Bone, Brain.
  // Every image here is Modality DX with RescaleSlope 1 and Intercept 0, so
  // stored values are detector output and not HU: Lung (1500/-600) and Brain
  // (80/40) are meaningless on a radiograph.
  //
  // Absolute values cannot be corrected either, because the three publishing
  // vendors work at 12, 14 and 16-bit and differ by up to 16x. These presets
  // are resolved against each image's own histogram instead. Measurements:
  // docs/superpowers/specs/2026-08-03-window-level-presets.md
  {
    commandName: 'equineWindowLevelPreset',
    commandOptions: { index: 1 },
    label: 'W/L Auto',
    keys: ['1'],
    isEditable: true,
  },
  {
    commandName: 'equineWindowLevelPreset',
    commandOptions: { index: 2 },
    label: 'W/L Bone Detail',
    keys: ['2'],
    isEditable: true,
  },
  {
    commandName: 'equineWindowLevelPreset',
    commandOptions: { index: 3 },
    label: 'W/L Soft Tissue',
    keys: ['3'],
    isEditable: true,
  },
  {
    commandName: 'equineWindowLevelPreset',
    commandOptions: { index: 4 },
    label: 'W/L Wide',
    keys: ['4'],
    isEditable: true,
  },
  {
    commandName: 'equineWindowLevelPreset',
    commandOptions: { index: 5 },
    label: 'W/L High Contrast',
    keys: ['5'],
    isEditable: true,
  },
  // Upstream carried commented-out presets 6-9 here, keyed off
  // windowLevelPresets. Removed with the CT presets above: 6-9 are free for
  // whatever earns them. windowLevelPresets itself stays, because
  // platform/core/src/defaults/index.js exports it and the basic-test-mode
  // and basic-dev-mode toolbars still consume it.
];

export default bindings;
